/**
 * Console Override Utility
 *
 * Suppresses console.log, console.info, and console.debug in production
 * while preserving console.warn and console.error in ALL environments.
 *
 * Usage: Call initConsoleOverride() at application entry point before any console usage.
 */

// Store original console methods
const originalConsole = {
  log: console.log,
  info: console.info,
  debug: console.debug,
  warn: console.warn,
  error: console.error,
};

/**
 * Determines if the current environment is production.
 * Checks NEXT_PUBLIC_APP_ENV first (for Next.js apps), then NODE_ENV (for build-time).
 */
function isProduction(): boolean {
  // Direct check for baked-in variables (works best with esbuild/tsup define)
  try {
    if (process.env.NEXT_PUBLIC_APP_ENV === 'production') return true;
    if (process.env.NODE_ENV === 'production') return true;
  } catch (e) {
    // If process.env is totally missing
  }

  return false;
}

/**
 * Initializes console override.
 * Must be called at application entry point before any console usage.
 */
export function initConsoleOverride(): void {
  const isProd = isProduction();

  if (isProd) {
    // Suppress console.log, info, and debug in production
    console.log = () => {};
    console.info = () => {};
    console.debug = () => {};

    // Keep console.warn and console.error functional
    console.warn = originalConsole.warn;
    console.error = originalConsole.error;
  } else {
    // In dev/staging, ensure all console methods use originals
    console.log = originalConsole.log;
    console.info = originalConsole.info;
    console.debug = originalConsole.debug;
    console.warn = originalConsole.warn;
    console.error = originalConsole.error;
  }
}

/**
 * Restores original console methods.
 * Useful for testing or rollback scenarios.
 */
export function restoreConsole(): void {
  console.log = originalConsole.log;
  console.info = originalConsole.info;
  console.debug = originalConsole.debug;
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
}
